import { capitalize } from 'lodash';
import { Drawer, DrawerProps, Form, Space } from 'antd';
import { useFormik } from 'formik';
import Button from 'components/new-ui/base/button/Button';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import { NewTeamFormValues } from 'views/teams/types';
import { newTeamFormValidationSchema } from 'views/teams/validation-schemas';
import { createTeam } from 'store/teams/client';
import { declarationCountryLabels } from 'store/declarations/enums/common/declaration-country';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import Notification from 'components/ui/base/notification/Notification';

const initialValues: NewTeamFormValues = {
    name: '',
    market: null,
};

interface Props extends DrawerProps {
    onSuccess?: () => void;
}

const NewTeamFormDrawer = ({ onSuccess, onClose, ...rest }: Props) => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const formik = useFormik<NewTeamFormValues>({
        initialValues,
        validationSchema: newTeamFormValidationSchema,
        onSubmit: async ({ market, name }) => {
            try {
                showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Creating a team...' } });

                if (!name || !market) {
                    throw new Error('Missing required data for the team');
                }

                await createTeam({
                    name,
                    market,
                });

                Notification({
                    type: 'success',
                    placement: 'top',
                    messageTitle: 'Team Created',
                    description: `Successfully created team "${name}"`,
                });

                onSuccess?.();
                formik.resetForm();
            } catch (error) {
                Notification({
                    type: 'error',
                    placement: 'top',
                    messageTitle: 'Error',
                    description: 'Could not create team. Please try again',
                });
            } finally {
                hideGlobalOverlay();
            }
        },
    });

    return (
        <Drawer
            title="New Team"
            maskClosable={false}
            onClose={onClose}
            extra={
                <Space>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        type="primary"
                        disabled={!formik.isValid && formik.dirty}
                        loading={formik.isSubmitting}
                        onClick={formik.submitForm}
                    >
                        Create
                    </Button>
                </Space>
            }
            {...rest}
        >
            <form onSubmit={formik.handleSubmit}>
                <Form layout="vertical">
                    <Space direction="vertical" size={24} style={{ width: '100%' }}>
                        <label>
                            <LabelRequiredSymbol style={{ marginRight: 8 }} />
                            Team Name
                            <FormInput
                                placeholder="Enter team name"
                                fieldProps={formik.getFieldProps('name')}
                                fieldMeta={formik.getFieldMeta('name')}
                                fieldHelper={formik.getFieldHelpers('name')}
                            />
                        </label>

                        <label>
                            <LabelRequiredSymbol style={{ marginRight: 8 }} />
                            Market
                            <FormSelect
                                hideKeys
                                placeholder="Select Market"
                                fieldHelper={formik.getFieldHelpers('market')}
                                fieldMeta={formik.getFieldMeta('market')}
                                fieldProps={formik.getFieldProps('market')}
                                selectOptions={declarationCountryLabels.map((el) => ({
                                    id: el.id,
                                    value: capitalize(el.value),
                                }))}
                            />
                        </label>
                    </Space>
                </Form>
            </form>
        </Drawer>
    );
};

export default NewTeamFormDrawer;
